/**
 * CitizenLinkDialog
 *
 * Dialog that shows URLs for citizens to access a process.
 * Includes direct process link and general authority link.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
  Divider,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  QrCode as QrCodeIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

interface CitizenLinkDialogProps {
  open: boolean;
  onClose: () => void;
  processKey: string;
  processName: string | null;
  authorityKeyId: string;
}

/**
 * Get the citizen app base URL from environment or default
 */
function getCitizenAppUrl(): string {
  if (import.meta.env.VITE_CITIZEN_APP_URL) {
    return import.meta.env.VITE_CITIZEN_APP_URL;
  }

  const origin = window.location.origin;

  // Production: amt.dev.kryptance.io -> antrag.dev.kryptance.io
  if (origin.includes('amt.dev.')) {
    return origin.replace('amt.dev.', 'antrag.dev.');
  }

  // Local development: port 5173 -> 5174
  return origin.replace(':5173', ':5174');
}

export function CitizenLinkDialog({
  open,
  onClose,
  processKey,
  processName,
  authorityKeyId,
}: CitizenLinkDialogProps) {
  const [copiedDirect, setCopiedDirect] = useState(false);
  const [copiedGeneral, setCopiedGeneral] = useState(false);

  const citizenAppUrl = getCitizenAppUrl();
  const directLink = `${citizenAppUrl}/submit/${authorityKeyId}/${processKey}`;
  const generalLink = `${citizenAppUrl}/submit/${authorityKeyId}`;

  const handleCopyDirect = async () => {
    try {
      await navigator.clipboard.writeText(directLink);
      setCopiedDirect(true);
      setTimeout(() => setCopiedDirect(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyGeneral = async () => {
    try {
      await navigator.clipboard.writeText(generalLink);
      setCopiedGeneral(true);
      setTimeout(() => setCopiedGeneral(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinkIcon color="primary" />
        Bürger-Link teilen
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Teilen Sie diese Links mit Bürgern, damit sie Anträge einreichen können.
        </Typography>

        {/* Direct Link */}
        <Typography variant="subtitle2" gutterBottom>
          Direktlink zu "{processName || processKey}"
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Bürger gelangen direkt zum ausgewählten Prozess.
        </Typography>
        <TextField
          fullWidth
          value={directLink}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copiedDirect ? 'Kopiert!' : 'Link kopieren'}>
                    <IconButton onClick={handleCopyDirect} edge="end">
                      {copiedDirect ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CopyIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ my: 2 }} />

        {/* General Link */}
        <Typography variant="subtitle2" gutterBottom>
          Allgemeiner Link (Prozessauswahl)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Bürger können aus allen verfügbaren Prozessen auswählen.
        </Typography>
        <TextField
          fullWidth
          value={generalLink}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copiedGeneral ? 'Kopiert!' : 'Link kopieren'}>
                    <IconButton onClick={handleCopyGeneral} edge="end">
                      {copiedGeneral ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CopyIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 3 }}
        />

        {/* QR Code Placeholder */}
        <Divider sx={{ my: 2 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'action.hover',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Box
            sx={{
              width: 100,
              height: 100,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <QrCodeIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Box>
            <Typography variant="subtitle2">QR-Code</Typography>
            <Typography variant="body2" color="text.secondary">
              Scannen Sie den QR-Code für mobilen Zugriff auf den Direktlink.
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          Diese Links sind öffentlich zugänglich. Jeder mit dem Link kann Anträge einreichen.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
